/**
 * PresentationWelcome — first-login Presentation Selector (Demo Mode only).
 *
 * On first arrival at the admin dashboard in a demo build, offers a guided,
 * AI-narrated presentation (Executive 2m / Operator 5m default / Showcase 10m)
 * or "Explore freely". Dismissible; remembered so it won't nag. Never rendered
 * in production (gated by the caller via SHOW_DEMO_TOOLS).
 */
import React, { useState } from "react";
import { Presentation, Clock, Compass, X, Sparkles, Volume2 } from "lucide-react";

const OPTIONS = [
  { id: "executive", label: "Executive Overview", duration: "≈ 2 min", desc: "For senior decision-makers — the headline value, fast." },
  { id: "operator", label: "Operator Demonstration", duration: "≈ 5 min", desc: "The standard sales walkthrough.", recommended: true },
  { id: "full", label: "Full Product Showcase", duration: "≈ 10 min", desc: "Detailed end-to-end tour of the whole platform." },
];

const VOICES = [
  { id: "uk_female", label: "UK English – Professional Female", default: true },
  { id: "uk_male", label: "UK English – Professional Male" },
  { id: "warm", label: "Warm Friendly" },
  { id: "executive", label: "Executive / Corporate" },
  { id: "neutral", label: "Clear Neutral" },
];

export default function PresentationWelcome({ open, onPick, onExplore }) {
  const [voiceId, setVoiceId] = useState(() => localStorage.getItem("demoNarrationVoice") || "uk_female");
  const changeVoice = (id) => {
    setVoiceId(id);
    try { localStorage.setItem("demoNarrationVoice", id); } catch (_) { /* ignore */ }
  };
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[90] bg-[#0b1f3a]/80 backdrop-blur-sm grid place-items-center p-4" data-testid="presentation-welcome">
      <div className="bg-card border border-border rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden">
        <div className="bg-[#0b1f3a] text-white p-5 relative">
          <button onClick={onExplore} className="absolute top-4 right-4 text-white/60 hover:text-white" data-testid="welcome-dismiss" aria-label="Dismiss">
            <X className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-2 text-[#5e9bff] text-xs font-bold tracking-[0.2em]">
            <Sparkles className="w-4 h-4" /> CRYSTAL DISPATCH
          </div>
          <h2 className="font-heading text-2xl font-black mt-2 leading-tight">Welcome to the demonstration</h2>
          <p className="text-white/70 text-sm mt-1.5">Take a self-guided, AI-narrated tour — or explore the platform yourself. You can start, pause or leave a presentation at any time.</p>
        </div>
        <div className="p-4 space-y-2.5">
          <div className="pb-1">
            <label htmlFor="welcome-voice" className="label-mini text-muted-foreground flex items-center gap-1.5 mb-1.5">
              <Volume2 className="w-3.5 h-3.5" /> Narration voice
            </label>
            <select
              id="welcome-voice"
              value={voiceId}
              onChange={(e) => changeVoice(e.target.value)}
              data-testid="welcome-voice-select"
              className="w-full p-2.5 text-sm rounded-lg border border-border bg-background focus:border-[#6D28D9] focus:outline-none"
            >
              {VOICES.map((v) => (
                <option key={v.id} value={v.id}>{v.label}{v.default ? " (recommended)" : ""}</option>
              ))}
            </select>
          </div>
          {OPTIONS.map((o) => (
            <button
              key={o.id}
              onClick={() => onPick(o.id)}
              data-testid={`welcome-profile-${o.id}`}
              className={`w-full text-left p-3.5 border rounded-xl transition-colors flex items-start gap-3 ${
                o.recommended ? "border-[#6D28D9] bg-[#6D28D9]/[0.05]" : "border-border hover:border-[#6D28D9]"
              }`}
            >
              <div className="w-9 h-9 grid place-items-center bg-[#6D28D9] text-white rounded-md flex-shrink-0">
                <Presentation className="w-4 h-4" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-semibold flex items-center gap-2">
                    {o.label}
                    {o.recommended && <span className="label-mini px-1.5 py-0.5 rounded-sm bg-[#6D28D9] text-white text-[9px]">RECOMMENDED</span>}
                  </span>
                  <span className="text-xs text-muted-foreground flex items-center gap-1 flex-shrink-0"><Clock className="w-3 h-3" />{o.duration}</span>
                </div>
                <p className="text-xs text-muted-foreground mt-0.5">{o.desc}</p>
              </div>
            </button>
          ))}
          <button
            onClick={onExplore}
            data-testid="welcome-explore-freely"
            className="w-full text-left p-3.5 border border-dashed border-border rounded-xl hover:border-foreground/30 transition-colors flex items-center gap-3"
          >
            <div className="w-9 h-9 grid place-items-center bg-muted text-foreground/70 rounded-md flex-shrink-0">
              <Compass className="w-4 h-4" />
            </div>
            <div>
              <span className="font-semibold">Explore freely</span>
              <p className="text-xs text-muted-foreground mt-0.5">Skip the tour and navigate the system yourself.</p>
            </div>
          </button>
        </div>
      </div>
    </div>
  );
}
